'use client';

import React from 'react';
import { styled } from '@linaria/react';

import { throttle } from '@/utils';
import useHasHydrated from '@/hooks/use-has-hydrated';
import { Heading, HeadingLevel } from '@/types/content.types';

interface Props extends React.HTMLAttributes<HTMLElement> {
  headings: Array<Heading>;
}

interface HeadingBox {
  id: string;
  box: DOMRect;
}

function TableOfContents({ headings, ...delegated }: Props) {
  const largeEnoughHeadings = headings.filter((h) => h.level <= 3);
  const hasHydrated = useHasHydrated();

  const activeHeadingId = useActiveHeading(largeEnoughHeadings);

  // If the 1st heading has the word “introduction” in it, don't
  // render our “introduction” pseudo-heading.
  const skipIntroduction = !!headings[0]?.text.match(/introduction/i);

  const handleClick = React.useCallback((event: React.MouseEvent) => {
    event.preventDefault();

    const target = event.target as HTMLAnchorElement;
    const href = target.getAttribute('href');

    if (!href) {
      // Shouldn't happen. Mostly for TS.
      return;
    }

    const elem = document.querySelector(href);

    if (elem) {
      elem.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, []);

  // For keyboard users, we don’t just want to scroll the heading into view, we want to move focus to its anchor. That way, they can navigate through the article starting at this point.
  const handleKeyUp = React.useCallback(
    (event: React.KeyboardEvent) => {
      event.preventDefault();

      if (event.key !== 'Enter') {
        return;
      }

      const target = event.target as HTMLAnchorElement;
      const href = target.getAttribute('href');

      if (!href) {
        // Shouldn't happen. Mostly for TS.
        return;
      }

      const elem = document.querySelector(href) as
        | HTMLAnchorElement
        | undefined;

      if (elem) {
        elem.focus({
          preventScroll: false,
        });
      }
    },
    []
  );

  return (
    <Wrapper
      {...delegated}
      style={{
        // Ugh, so scroll position is reset on hydration. This means that if the user clicks a link super fast, they're brought down to the correct spot, but then snapped back on hydration to the original position!
        // I thought maybe this was because of the experimental `scrollRestoration` config option, but it happens even without it!
        // And so, to prevent frustration, we’ll hide the TOC until after hydration. This kinda sucks, since it draws attention unnecessarily to the TOC, but it's better than the alternative.
        // TODO: It would be fun to do a cascade fade-in from top to bottom. Would also be more performant, no transforms. But really, if I'm going to spend time on this, maybe I should try to fix the scroll-jumping bug instead?
        opacity: hasHydrated ? 1 : 0,
        '--x': hasHydrated ? '0px' : '20px',
      }}
    >
      <TocHeading>Table of Contents</TocHeading>

      {!skipIntroduction && (
        <ContentLinkHeading
          href="#introduction"
          style={getStylesForDepth(2, !activeHeadingId)}
          onClick={handleClick}
          onKeyUp={handleKeyUp}
        >
          Introduction
        </ContentLinkHeading>
      )}

      {largeEnoughHeadings.map((heading, index) => (
        <ContentLinkHeading
          key={index}
          href={`#${heading.id}`}
          style={getStylesForDepth(
            heading.level,
            activeHeadingId === heading.id
          )}
          onClick={handleClick}
          onKeyUp={handleKeyUp}
        >
          {/* Some of my headings use backticks to add inline <code> to the heading. For now, the simplest thing is to remove these backticks altogether when displaying the heading link in the TOC */}
          {heading.text.replace(/`/g, '')}
        </ContentLinkHeading>
      ))}
    </Wrapper>
  );
}

const useActiveHeading = (headings: Array<Heading>) => {
  const [activeHeadingId, setActiveHeading] = React.useState<
    string | null
  >(null);

  React.useEffect(() => {
    const handleScroll = throttle(() => {
      // If we're all the way at the top, there is no active heading.
      // This is done because "Introduction", the first link in the TOC, will be active if `heading` is `null`.
      if (window.scrollY === 0) {
        return setActiveHeading(null);
      }

      // There HAS to be a better single-step algorithm for this, but I can't think of it. So I'm doing this in 2 steps:
      //
      // 1. Are there any headings in the viewport right now? If so,
      //    pick the top one.
      // 2. If there are no headings in the viewport, are there any
      //    above the viewport? If so, pick the last one (most
      //    recently scrolled out of view)
      //
      // If neither condition is met, I'll assume I'm still in the intro, although this would have to be a VERY long intro to ever be true.

      const headingBoxes = headings
        .map(({ id }) => {
          const elem = document.querySelector(`#${id}`);

          if (!elem) {
            return null;
          }

          return { id, box: elem.getBoundingClientRect() };
        })
        .filter((box): box is HeadingBox => !!box);

      // The first heading within the viewport is the one we want to highlight.Because our heading obscures the top ~100px of the window, I'm considering that range out-of-viewport.
      const TOP_OFFSET = 120;
      let firstHeadingInViewport = headingBoxes.find(({ box }) => {
        return (
          box.bottom > TOP_OFFSET && box.top < window.innerHeight
        );
      });

      // If there is no heading in the viewport, check and see if there are any
      // above the viewport.
      if (!firstHeadingInViewport) {
        const reversedBoxes = [...headingBoxes].reverse();

        firstHeadingInViewport = reversedBoxes.find(({ box }) => {
          return box.bottom < TOP_OFFSET;
        });
      }

      if (!firstHeadingInViewport) {
        setActiveHeading(null);
      } else if (firstHeadingInViewport.id !== activeHeadingId) {
        setActiveHeading(firstHeadingInViewport.id);
      }
    }, 500);

    // If the user refreshes the page while scrolled down, we need to change the *initial* active heading. Run the same calculation on mount:
    handleScroll();

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [activeHeadingId, headings]);

  return activeHeadingId;
};

const getStylesForDepth = (
  level: HeadingLevel,
  isActiveHeading: boolean
) => {
  const base = {
    color: isActiveHeading ? 'var(--color-primary)' : undefined,
    opacity: isActiveHeading ? 1 : undefined,
  };

  switch (level) {
    case 2:
      return {
        ...base,
        marginTop: 12,
        '--font-size-px': 16,
      };

    case 3:
      return {
        ...base,
        marginTop: 4,
        '--font-size-px': 14,
        paddingLeft: 12,
      };

    case 4:
      return {
        ...base,
        marginTop: 4,
        '--font-size-px': 14,
        paddingLeft: 24,
      };

    default:
      throw new Error('Unsupported heading size: ' + level);
  }
};

const Wrapper = styled.nav`
  transition:
    opacity 400ms,
    transform 700ms cubic-bezier(0.17, 0.67, 0.54, 1);
  transform: translateX(var(--x));
`;

const TocHeading = styled.h2`
  color: var(--color-gray-900);
  font-size: 1rem;
  margin-bottom: 16px;
  font-weight: var(--font-weight-medium);
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const ContentLinkHeading = styled.a`
  display: block;
  opacity: 0.7;
  color: var(--color-gray-900);
  text-decoration: none;
  transition: opacity 500ms;
  font-size: calc(var(--font-size-px) / 16 * 1rem);
  line-height: 1.5;

  &:hover,
  &:focus {
    opacity: 1;
    transition: opacity 0ms;
  }
`;

export default TableOfContents;
